import type { AuthTokens, JwtClaims } from '@/lib/api/types';

/**
 * In-memory session store (plan §4.2 / §17 security).
 *
 * The access token lives ONLY here — never localStorage/sessionStorage. The refresh token
 * lives in an httpOnly cookie set by the backend; if the backend instead returns it in the
 * login/refresh body, we keep it in this module variable (still never persisted to web
 * storage). On hard reload the cookie-based refresh re-establishes the session.
 */
interface SessionState {
  accessToken: string | null;
  refreshToken: string | null;
  expiresAt: number | null; // epoch ms
  claims: JwtClaims | null;
}

const state: SessionState = {
  accessToken: null,
  refreshToken: null,
  expiresAt: null,
  claims: null,
};

type Listener = () => void;
const listeners = new Set<Listener>();
function emit() {
  for (const l of listeners) l();
}

export function subscribeSession(l: Listener): () => void {
  listeners.add(l);
  return () => listeners.delete(l);
}

/** Decode a JWT payload without verifying the signature (server is authoritative). */
export function decodeJwt(token: string): JwtClaims | null {
  try {
    const payload = token.split('.')[1];
    const json = atob(payload.replace(/-/g, '+').replace(/_/g, '/'));
    return JSON.parse(json) as JwtClaims;
  } catch {
    return null;
  }
}

/**
 * A durable, non-secret "this browser has signed in before" marker.
 *
 * The refresh cookie is httpOnly, so the app cannot see whether it has one. Without this
 * marker every cold boot POSTs `/auth/refresh` to find out — including boots by someone who
 * has never signed in on this device, and every load of the sign-in screen itself. The
 * unauthenticated auth endpoints are rate-limited per IP, so on a shared office address
 * signed-out page loads would spend the budget the staff need to sign in.
 *
 * It holds no secret and grants nothing: worst case it is stale, and either one wasted
 * refresh happens (marker without a cookie — the 401 then clears it) or the user signs in
 * again (cookie without a marker, e.g. storage cleared). It is therefore safe to keep in
 * localStorage, which the access token never is.
 */
export const SESSION_MARKER_KEY = 'tepco.hasSession';

/** localStorage throws in some privacy modes; a missing marker is a safe answer. */
function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}

export function hasSessionMarker(): boolean {
  return safeStorage()?.getItem(SESSION_MARKER_KEY) === '1';
}

function setSessionMarker(present: boolean): void {
  const store = safeStorage();
  if (!store) return;
  try {
    if (present) store.setItem(SESSION_MARKER_KEY, '1');
    else store.removeItem(SESSION_MARKER_KEY);
  } catch {
    /* Quota or a locked-down profile — the marker is an optimisation, not state. */
  }
}

export function setTokens(tokens: AuthTokens): void {
  state.accessToken = tokens.access_token;
  state.refreshToken = tokens.refresh_token ?? state.refreshToken;
  state.expiresAt = new Date(tokens.expires_at).getTime();
  state.claims = decodeJwt(tokens.access_token);
  setSessionMarker(true);
  emit();
}

export function clearSession(): void {
  state.accessToken = null;
  state.refreshToken = null;
  state.expiresAt = null;
  state.claims = null;
  setSessionMarker(false);
  emit();
}

export function getAccessToken(): string | null {
  return state.accessToken;
}

export function getRefreshToken(): string | null {
  return state.refreshToken;
}

export function getClaims(): JwtClaims | null {
  return state.claims;
}

export function isAuthenticated(): boolean {
  return state.accessToken !== null;
}

/** Milliseconds until the access token expires (negative if already expired). */
export function timeUntilExpiry(): number {
  if (state.expiresAt == null) return Infinity;
  return state.expiresAt - Date.now();
}

export function getSessionSnapshot(): Readonly<SessionState> {
  return state;
}
